import { createBrowserClient } from '@supabase/ssr';
import type { Database } from './database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL!;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY!;

console.log('Using Supabase URL:', supabaseUrl);

if (!supabaseUrl) {
  throw new Error('Missing VITE_SUPABASE_URL environment variable');
}

if (!supabaseAnonKey) {
  throw new Error('Missing VITE_SUPABASE_ANON_KEY environment variable');
}

// This is the cookie name GoTrue seems to be using by default in local CLI dev
const actualCookieName = 'sb-localhost-auth-token';

/**
 * @ai-context Supabase Client Configuration for Local Subdomain Auth
 * Purpose: Initializes the Supabase client for browser-side usage with SSR capabilities.
 * Location: src/lib/supabase.ts
 *
 * CRITICAL FOR OAUTH (LinkedIn, Google, etc.):
 * - The SSR client uses cookies to store PKCE code_verifier during OAuth flows
 * - maxAge MUST be set (e.g., 3600) - NOT undefined
 * - If maxAge is undefined, session cookies may not persist across OAuth redirects
 * - This causes "flow_state_not_found" errors during token exchange
 * - See docs/LINKEDIN_OAUTH_TROUBLESHOOTING.md for detailed debugging info
 *
 * Constraints for Local Development (Subdomain Authentication):
 * - GoTrue (via Supabase CLI) defaults to cookie name 'sb-localhost-auth-token'.
 * - GoTrue sets a cookie domain (e.g., .local.terrakube.com or local.terrakube.com)
 *   that allows sharing with subdomains when GOTRUE_SITE_URL is configured appropriately.
 * - These client-side `cookieOptions` (name, domain) MUST align with GoTrue's
 *   actual behavior in the local CLI environment for cross-subdomain auth to work.
 * - Attempts to force GoTrue's cookie name/domain via GOTRUE_COOKIE_* env vars in
 *   docker-compose.override.yml were found to be ineffective with the local Supabase CLI.
 */
// Determine if we're in production or development
const isProduction = import.meta.env.PROD;
const isDevelopment = import.meta.env.DEV;

// Detect if we're on a custom domain (not *.terrarium.app)
const isCustomDomain = () => {
  const hostname = window.location.hostname;
  const appDomain = import.meta.env.VITE_APP_DOMAIN || 'terrarium.app';
  // Custom domain = not ending with appDomain and not localhost/dev domains
  return (
    !hostname.endsWith(appDomain) &&
    !hostname.endsWith('.lvh.me') &&
    hostname !== 'lvh.me' &&
    hostname !== 'localhost' &&
    hostname !== '127.0.0.1'
  );
};

// Get the appropriate cookie configuration based on environment
const getCookieOptions = () => {
  if (isDevelopment) {
    return {
      name: actualCookieName,
      domain: '.lvh.me', // lvh.me auto-resolves to 127.0.0.1, no /etc/hosts needed
      path: '/',
      sameSite: 'lax' as const,
      secure: false,
      httpOnly: false,
      // Set maxAge to 1 hour (3600 seconds) to ensure PKCE code_verifier
      // cookie persists across OAuth redirects. Without this, session cookies
      // may not survive the LinkedIn OAuth redirect flow.
      maxAge: 3600,
    };
  }

  // Production settings
  const appDomain = import.meta.env.VITE_APP_DOMAIN || 'terrarium.app';

  // For custom domains (e.g., hugthebot.com), don't set a specific domain
  // This allows the cookie to be set on the current domain only
  // For *.terrarium.app, use the shared domain for cross-subdomain auth
  if (isCustomDomain()) {
    console.log(
      '[supabase.ts] Custom domain detected, using current domain for cookies:',
      window.location.hostname
    );
    return {
      // No domain specified = cookie is set for current domain only
      path: '/',
      sameSite: 'lax' as const,
      secure: true, // HTTPS in production
      httpOnly: false,
      maxAge: 3600,
    };
  }

  return {
    // For *.terrarium.app subdomains, share cookies across all subdomains
    domain: `.${appDomain}`, // Leading dot for cross-subdomain cookies
    path: '/',
    sameSite: 'lax' as const,
    secure: true, // HTTPS in production
    httpOnly: false,
    // Set maxAge to 1 hour (3600 seconds) for OAuth PKCE flow
    maxAge: 3600,
  };
};

// Use createBrowserClient from @supabase/ssr
export const supabase = createBrowserClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      // General auth options
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
    cookieOptions: getCookieOptions(),
  }
);

console.log(
  'Supabase client created using @supabase/ssr createBrowserClient with cookie name:',
  actualCookieName
);

// Basic listener (optional here, as useAuth handles it)
supabase.auth.onAuthStateChange((event, session) => {
  console.log('[supabase.ts] Auth state change event:', event);
  // Note: With HttpOnly cookies, direct document.cookie access for the auth token might be restricted.
  // The ssr client handles session via cookies internally.
});

// Get consistent base URL for auth redirects
export function getBaseUrl() {
  // In production, always use the actual origin but ensure no www to prevent redirect loops
  if (import.meta.env.PROD) {
    const origin = window.location.origin;
    // Remove www. to prevent redirect loops with useWwwRedirect
    if (origin.includes('www.terrarium.app')) {
      return origin.replace('www.', '');
    }
    return origin;
  }

  // Development environment handling
  // Check for lvh.me domain (auto-resolves to 127.0.0.1 via public DNS)
  if (
    window.location.hostname.endsWith('.lvh.me') ||
    window.location.hostname === 'lvh.me'
  ) {
    return `http://lvh.me:${window.location.port}`;
  }
  // Fallback for actual localhost if needed, though primary use will be new domain
  if (
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === 'localhost'
  ) {
    return `http://localhost:${window.location.port}`;
  }

  // Default fallback
  return window.location.origin;
}
